import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiCheck } from 'react-icons/hi';
import { topicsService } from '../../services/topicsService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import ExpandableText from './ExpandableText';

function TopicsManagement({ selectedEdition }) {
  const [topicsData, setTopicsData] = useState({
    editionInfo: {
      editionNumber: '',
      themeTitle: '',
      themeSubtitle: ''
    },
    congressHeader: {
      title: 'Axes du Congrès',
      subtitle: ''
    },
    topics: []
  });
  
  const [editingTopic, setEditingTopic] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTopic, setNewTopic] = useState({
    number: '',
    title: '',
    description: '',
    color: '#002b5b'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadTopicsData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(topicsData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [topicsData, originalData]);

  const loadTopicsData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading topics data for edition:', selectedEdition);
      const data = await topicsService.getTopicsData(selectedEdition);
      
      if (!data || ((!data.editionInfo?.editionNumber || data.editionInfo.editionNumber === '') && 
                    (!data.editionInfo?.themeTitle || data.editionInfo.themeTitle === '') &&
                    (!data.congressHeader?.title || data.congressHeader.title === 'Axes du Congrès') && 
                    (!data.congressHeader?.subtitle || data.congressHeader.subtitle === '') &&
                    (!data.topics || data.topics.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          editionInfo: {
            editionNumber: `${selectedEdition}ème Édition`,
            themeTitle: 'Thème du Congrès',
            themeSubtitle: 'Sous-titre du thème'
          },
          congressHeader: {
            title: 'Axes du Congrès',
            subtitle: 'Découvrez les différents axes de recherche'
          },
          topics: []
        };
        setTopicsData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setTopicsData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading topics data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        editionInfo: {
          editionNumber: `${selectedEdition}ème Édition`,
          themeTitle: 'Thème du Congrès',
          themeSubtitle: 'Sous-titre du thème'
        },
        congressHeader: {
          title: 'Axes du Congrès',
          subtitle: 'Découvrez les différents axes de recherche'
        },
        topics: []
      };
      setTopicsData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
  };

  const handleDeleteTopic = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet axe ?')) {
      try {
        await topicsService.deleteTopic(id);
        setTopicsData(prev => ({
          ...prev,
          topics: prev.topics.filter(topic => topic.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting topic:', error);
        alert('Erreur lors de la suppression de l\'axe.');
      }
    }
  };

  const handleSaveTopic = async (updatedTopic) => {
    try {
      await topicsService.updateTopic(updatedTopic.id, updatedTopic);
      setTopicsData(prev => ({
        ...prev,
        topics: prev.topics.map(topic =>
          topic.id === updatedTopic.id ? { ...topic, ...updatedTopic } : topic
        )
      }));
      setEditingTopic(null);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating topic:', error);
      alert('Erreur lors de la modification de l\'axe.');
    }
  };

  const handleAddTopic = async () => {
    try {
      // Auto-generate number based on existing topics count
      const nextNumber = ` ${(topicsData.topics.length + 1).toString().padStart(2, '0')}`;
      const topicWithNumber = { ...newTopic, number: nextNumber };
      
      if (isDatabaseEmpty) {
        // Add to local state with temporary ID
        const tempTopic = {
          ...topicWithNumber,
          id: `temp_${Date.now()}`,
          displayOrder: topicsData.topics.length
        };
        setTopicsData(prev => ({
          ...prev,
          topics: [...prev.topics, tempTopic]
        }));
        setHasUnsavedChanges(true);
      } else {
        const newTopicFromDb = await topicsService.addTopic(topicWithNumber, selectedEdition);
        const formattedTopic = {
          id: newTopicFromDb.id,
          number: newTopicFromDb.number,
          title: newTopicFromDb.title,
          description: newTopicFromDb.description,
          color: newTopicFromDb.color,
          displayOrder: newTopicFromDb.display_order
        };
        
        setTopicsData(prev => ({
          ...prev,
          topics: [...prev.topics, formattedTopic]
        }));
        showSuccessMessage();
      }
      
      setNewTopic({ number: '', title: '', description: '', color: '#002b5b' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding topic:', error);
      alert('Erreur lors de l\'ajout de l\'axe.');
    }
  };

  const handleSave = async () => {
    try {
      await topicsService.updateTopicsPageData(topicsData, selectedEdition);
      
      // Save topics if any with temp IDs
      for (const topic of topicsData.topics) {
        if (!topic.id || topic.id.toString().startsWith('temp_')) {
          await topicsService.addTopic(topic, selectedEdition);
        }
      }
      
      setOriginalData(JSON.parse(JSON.stringify(topicsData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
      
      // Reload data to get proper IDs from database
      setTimeout(() => {
        loadTopicsData();
      }, 1000);
    } catch (error) {
      console.error('Error saving topics data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setTopicsData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleUpdateEditionInfo = (field, value) => {
    setTopicsData(prev => ({
      ...prev,
      editionInfo: { 
        ...prev.editionInfo, 
        [field]: value 
      }
    }));
  };

  const handleUpdateCongressHeader = (field, value) => {
    setTopicsData(prev => ({
      ...prev,
      congressHeader: { 
        ...prev.congressHeader, 
        [field]: value 
      }
    }));
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Topics est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Edition Info Section */}
      <div className="section-header">
        <h3>Informations de l'Édition</h3>
      </div>
      <div className="form-group">
        <label>Numéro d'édition:</label>
        <input 
          type="text" 
          value={topicsData.editionInfo.editionNumber}
          onChange={(e) => handleUpdateEditionInfo('editionNumber', e.target.value)}
          placeholder="Ex: 5ème Édition"
        />
      </div>
      <div className="form-group">
        <label>Titre du thème:</label>
        <input 
          type="text" 
          value={topicsData.editionInfo.themeTitle}
          onChange={(e) => handleUpdateEditionInfo('themeTitle', e.target.value)}
          placeholder="Ex: Innovation et Recherche Médicale"
        />
      </div>
      <div className="form-group">
        <label>Sous-titre du thème:</label>
        <textarea 
          value={topicsData.editionInfo.themeSubtitle}
          onChange={(e) => handleUpdateEditionInfo('themeSubtitle', e.target.value)}
          placeholder="Description du thème principal"
        />
      </div>

      {/* Congress Header Section */}
      <div className="section-header">
        <h3>En-tête des Axes</h3>
      </div>
      <div className="form-group">
        <label>Titre principal:</label>
        <input 
          type="text" 
          value={topicsData.congressHeader.title}
          onChange={(e) => handleUpdateCongressHeader('title', e.target.value)}
          placeholder="Ex: Axes du Congrès"
        />
      </div>
      <div className="form-group">
        <label>Sous-titre:</label>
        <textarea 
          value={topicsData.congressHeader.subtitle}
          onChange={(e) => handleUpdateCongressHeader('subtitle', e.target.value)}
          placeholder="Description des axes de recherche"
        />
      </div>

      {/* Topics Management Section */}
      <div className="section-header">
        <h3>Gestion des Axes</h3>
        <button className="add-btn" onClick={() => setShowAddForm(true)}>
          <HiPlus /> Ajouter un axe
        </button>
      </div>

      {/* Add Topic Form */}
      {showAddForm && (
        <div className="add-form">
          <div className="form-group">
            <label>Titre:</label>
            <input 
              type="text" 
              value={newTopic.title}
              onChange={(e) => setNewTopic({...newTopic, title: e.target.value})}
              placeholder="Titre de l'axe"
              required
            />
          </div>
          <div className="form-group">
            <label>Description:</label>
            <textarea 
              value={newTopic.description}
              onChange={(e) => setNewTopic({...newTopic, description: e.target.value})}
              placeholder="Description détaillée de l'axe"
              required
            />
          </div>
          <div className="form-group">
            <label>Couleur:</label>
            <input 
              type="color" 
              value={newTopic.color}
              onChange={(e) => setNewTopic({...newTopic, color: e.target.value})}
            />
          </div>
          <div className="form-actions">
            <button onClick={handleAddTopic} className="btn-primary">
              <HiPlus /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn-secondary">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <DataTable 
          value={topicsData.topics} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun axe trouvé."
        >
          <Column 
            field="number" 
            header="Numéro" 
            body={(rowData) => (
              editingTopic?.id === rowData.id ? rowData.number : rowData.number
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="title" 
            header="Titre" 
            body={(rowData) => (
              editingTopic?.id === rowData.id ? (
                <input 
                  type="text" 
                  value={editingTopic.title}
                  onChange={(e) => setEditingTopic({...editingTopic, title: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%' }}
                />
              ) : <strong>{rowData.title}</strong>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="description" 
            header="Description" 
            body={(rowData) => (
              editingTopic?.id === rowData.id ? (
                <textarea 
                  value={editingTopic.description}
                  onChange={(e) => setEditingTopic({...editingTopic, description: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%', minHeight: '60px' }}
                />
              ) : <ExpandableText text={rowData.description || ''} maxChars={100} />
            )}
            style={{ minWidth: '300px' }}
          />
          <Column 
            header="Couleur" 
            body={(rowData) => (
              editingTopic?.id === rowData.id ? (
                <input 
                  type="color" 
                  value={editingTopic.color}
                  onChange={(e) => setEditingTopic({...editingTopic, color: e.target.value})}
                />
              ) : (
                <div 
                  style={{ 
                    width: '50px', 
                    height: '25px', 
                    backgroundColor: rowData.color, 
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                ></div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              editingTopic?.id === rowData.id ? (
                <div className="flex gap-2">
                  <Button icon="pi pi-check" onClick={() => handleSaveTopic(editingTopic)} rounded outlined severity="success" size="small" />
                  <Button icon="pi pi-times" onClick={() => setEditingTopic(null)} rounded outlined severity="secondary" size="small" />
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button icon="pi pi-pencil" onClick={() => handleEditTopic(rowData)} rounded outlined severity="info" size="small" />
                  <Button icon="pi pi-trash" onClick={() => handleDeleteTopic(rowData.id)} rounded outlined severity="danger" size="small" />
                </div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>
    </div>
  );
}


export default TopicsManagement;
